import React, { useState, useEffect } from 'react';
import Nav from '../LandingV2/Nav';
import Background from '../LandingV2/Background';
import useTheme from '../LandingV2/hooks/useTheme';
import '../LandingV2/landing-v2.css';
import './docs.css';

/**
 * Docs — public-facing knowledge base. The 5-minute explainer + role
 * walkthroughs + glossary. Designed to be linkable from the landing
 * ("Read the docs →") and from in-app help tooltips in metha-admin-v2.
 *
 * Routed at /docs. Shares Nav + Background with LandingV2 for visual
 * continuity. Content is hardcoded EN for now — the i18n wrapper can be
 * added later by wrapping strings in translate() like FAQ.js does.
 *
 * Each top-level section gets an HTML id so it's deep-linkable
 * (e.g. /docs#auditor) and the sticky side-nav can scroll to it.
 */
export default function Docs() {
  const [theme, toggleTheme] = useTheme();
  const [activeSection, setActiveSection] = useState('overview');

  // Highlight the side-nav entry for whichever section is most visible.
  // Simple intersection-observer based active-section tracker.
  useEffect(() => {
    const sections = document.querySelectorAll('.docs-section');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActiveSection(e.target.id);
        });
      },
      { rootMargin: '-30% 0px -60% 0px' }
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="lv2-root" data-theme={theme}>
      <Background />
      <Nav theme={theme} onToggleTheme={toggleTheme} />

      <main className="docs-shell">
        <DocsSideNav active={activeSection} />

        <article className="docs-content">

          <header className="docs-hero">
            <span className="docs-eyebrow">DOCUMENTATION · 5-MINUTE READ</span>
            <h1>Metha Biofund, explained</h1>
            <p className="docs-lede">
              A decentralised protocol that funds biomedical research through periodic
              token auctions and multi-auditor milestone consensus. Built for labs that
              do real work, auditors with skin in the game, and contributors who want
              their funding decisions on-chain.
            </p>
          </header>

          <Section id="overview" title="The big picture">
            <p>
              Funding biomedical research is broken in three ways at once:
              <strong> incentives are misaligned</strong> (grants reward writing, not delivery),
              <strong> review is opaque</strong> (peer review happens behind closed doors), and
              <strong> capital is concentrated</strong> (a handful of agencies and corporates pick winners).
            </p>
            <p>
              Metha replaces that pipeline with three on-chain primitives:
            </p>
            <Bullets items={[
              <><strong>An auction</strong> — anyone can contribute ETH to a 180-second period; that period's METH supply is split pro-rata among contributors. No bonding curve, no insider allocation.</>,
              <><strong>A request-and-review pool</strong> — labs submit milestone-based research proposals; auditing firms apply, vote on the proposal, and review proof-of-completion before each tranche releases. Disputes go to multi-auditor consensus (2/3 majority).</>,
              <><strong>A governance layer</strong> — METH holders vote on protocol-level parameter changes via snapshot voting (your weight is the balance you held at proposal time, not the balance you have when you vote — prevents transfer-and-vote manipulation).</>,
            ]} />
            <Callout tone="ctx">
              Live on <strong>Sepolia testnet</strong> since 2026-05-13. Mainnet is gated on legal +
              code + financial audits — see the roadmap.
            </Callout>
          </Section>

          <Section id="how" title="How a research request flows on-chain">
            <p>
              The state machine for a research request goes through these steps:
            </p>
            <Numbered items={[
              <><strong>Submit</strong> · a lab posts a research proposal with milestone names, ETH amounts, optional typed milestone categories (ClinicalData, Manufacturing, Regulatory, etc.), and an anti-spam deposit of 0.005 ETH.</>,
              <><strong>Hype</strong> · METH holders weight which requests deserve priority. Their vote weight is their current METH balance.</>,
              <><strong>Apply</strong> · registered auditor firms apply to audit. The contract filters by category compatibility (a clinical-trial proposal needs auditors with the ClinicalSafety category) and seniority caps (Junior firms can't sign off on milestones over 5 ETH; Senior over 100 ETH).</>,
              <><strong>Cast vote</strong> · each assigned auditor casts a verdict with per-milestone amount adjustments. The final amounts are Condorcet-averaged across all auditors.</>,
              <><strong>Accept</strong> · the lab accepts or rejects the verdict. If rejected, the 0.005 ETH deposit is reclaimable. If accepted, milestone 0 unlocks automatically.</>,
              <><strong>Claim</strong> · the lab claims the current milestone's ETH allocation.</>,
              <><strong>Submit proof</strong> · the lab submits either a single bytes32 hash or a typed deliverables array (StudyReport / DataPackage / IPAssignment / Prototype / TechTransfer + URI + evidence hash). Up to 10 deliverables per milestone.</>,
              <><strong>Review</strong> · auditors approve or reject the proof. 2/3 majority moves the milestone to Approved and unlocks the next one. Disputed proofs return to the lab for resubmission.</>,
              <><strong>Iterate</strong> · repeat steps 6–8 for every remaining milestone.</>,
            ]} />
          </Section>

          <Section id="roles" title="What can I do — by role">
            <RoleCard role="Visitor" persona="Curious onlooker">
              <p>You don't need a wallet to read the protocol's state.</p>
              <ul>
                <li>Browse the live auction at <a className="acc" href="/">metha.life</a> — current period, total raised, contributor breakdown.</li>
                <li>Read the whitepaper + due-diligence doc in the <a className="acc" href="#papers">Papers section</a>.</li>
                <li>Explore the dapp read-only at <code>sepolia.metha.life</code> — every page works without a wallet, just without write actions.</li>
              </ul>
            </RoleCard>

            <RoleCard role="Contributor" persona="Wants to fund research">
              <p>Send ETH to <code>MethaCrowdsale</code> during a 180-second period; receive a pro-rata share of that period's METH allocation.</p>
              <Bullets items={[
                <>Connect MetaMask on Sepolia (chainId <code>11155111</code>).</>,
                <>Open <code>/auction</code> in the dapp; you'll see the live countdown, current contribution, and your projected METH share.</>,
                <>Click "Contribute"; sign the transaction. ETH stays in the contract until the period closes, at which point your METH becomes claimable.</>,
                <>Claim at <code>/account</code>: "Claim all" for everything in one tx, or use the granular panel to claim specific periods or ranges if you have many.</>,
              ]} />
              <Callout tone="amber">
                You need ETH on Sepolia. Use a <a className="acc" href="https://sepoliafaucet.com/" target="_blank" rel="noreferrer">Sepolia faucet</a> if you don't have any.
              </Callout>
            </RoleCard>

            <RoleCard role="Lab / researcher" persona="Wants funding for a research project">
              <p>Submit a milestone-based request; if auditors vote to approve, you claim ETH tranches as you hit each milestone.</p>
              <Bullets items={[
                <>Buy enough METH at the auction to cover the anti-spam deposit (0.005 ETH) and to participate in governance if relevant.</>,
                <>Open <code>/request/new</code>. Fill in title, description, IPFS hash of your full proposal, and the milestone schedule. For each milestone you can declare a <strong>MilestoneType</strong> (Scientific / Regulatory / ClinicalData / Manufacturing / Commercial / Operational) so the right auditors get filtered to your request.</>,
                <>Toggle <strong>option-period gating</strong> if you want each milestone to require a Program Officer (governance) approval before unlocking — this is the BARDA-style pattern.</>,
                <>Submit; pay the 0.005 ETH deposit. Your request goes live; auditors with relevant categories can apply.</>,
                <>Wait for the verdict (typically the auction-period cadence). If approved, click "Accept verdict" → "Claim milestone 0" → execute the work → "Submit proof" (typed deliverables or single hash) → wait for auditor review → next milestone unlocks.</>,
              ]} />
            </RoleCard>

            <RoleCard role="Auditor firm" persona="Has domain expertise, wants paid work">
              <p>Stake METH as a firm, declare your audit categories + seniority tier, and review research milestones for a fee.</p>
              <Bullets items={[
                <>Acquire METH via the auction (or transfer in). You need at least the tier minimum: <strong>10 METH (Junior)</strong>, 100 METH (Senior), or 1000 METH (Principal).</>,
                <>Open <code>/auditors</code>; click "Register firm…". Pick your jurisdiction (ISO-3166), seniority tier, and declare your audit categories. Approve + register.</>,
                <>Once registered, browse <code>/auditing</code> and apply to relevant requests. Compatibility is enforced on-chain — you can only apply to requests whose milestones match your declared categories and your value cap.</>,
                <>Cast votes on verdicts when assigned. Review proofs (approve or reject). Earn METH per successful sign-off (paid from the operations stash) and accumulate reputation.</>,
                <>Manage your firm at <code>/auditors</code>: increase stake, request unstake (7-day cooldown), upgrade seniority tier, add/remove categories.</>,
              ]} />
              <Callout tone="red">
                Bad-faith votes get slashed. Voting against the consensus (when 2/3 of your peers go the other way) costs you a percentage of your stake — Minor 3-10%, Major 10-50%, Critical 100% + permanent ban.
              </Callout>
            </RoleCard>

            <RoleCard role="Holder / governance" persona="Owns METH, wants protocol influence">
              <p>METH gives you weight in two parallel systems: project hype (which research to fund) and governance (how the protocol evolves).</p>
              <Bullets items={[
                <>At <code>/auditing</code> click any open request → "Vote hype". Your METH balance gets added to that request's hype score, which influences auditor attention priority.</>,
                <>At <code>/governance</code>, see open proposals. Each proposal is a list of <strong>slot rebinds</strong> — eg "swap MethaToken slot to address 0x…". Vote YES if you support; voting power is your METH balance at the proposal's snapshot block.</>,
                <>Holders also signal direction informally — discussion in the community channels, comments on proposals, and outreach all matter when the model evolves.</>,
              ]} />
              <Callout tone="ctx">
                Snapshot voting means buying METH AFTER a proposal is submitted gives you zero voting power on it. To vote you need to have held METH BEFORE the proposal's snapshot block.
              </Callout>
            </RoleCard>

            <RoleCard role="Oracle / compliance" persona="Trusted attestation authority">
              <p>Reserved for designated wallets (compliance partners, regulatory liaisons). Issue and revoke firm attestations like GxP, ISO 17025, KYB; grant DSMB veto rights to firms qualified to oversee clinical work.</p>
              <Bullets items={[
                <>The oracle role is granted by the contract owner (currently the deployer; transitions to OpenGovernance multisig post-deploy).</>,
                <>Open <code>/compliance</code> with your oracle wallet. The page detects your role and unlocks the action buttons; non-oracle wallets see the panel read-only.</>,
                <>Issue an attestation: pick a firm, pick a cert key (GxP / ISO 17025 / KYB / GMP / MDR / CFR 21 Part 11 / CLIA / custom), supply the evidence hash + expiry date. Issued.</>,
                <>Grant DSMB veto rights to firms already holding the ClinicalSafety category — they can then halt clinical-trial milestones on patient-safety grounds.</>,
                <>Slashing is owner-only (governance) and requires typing "SLASH CRITICAL" to confirm destructive actions.</>,
              ]} />
            </RoleCard>
          </Section>

          <Section id="mechanisms" title="Key on-chain mechanisms">
            <Mechanism title="§3.1 — Multi-auditor consensus">
              <p>Each accepted research request has up to <code>MAX_AUDITORS_PER_REQUEST</code> auditors. Verdicts and milestone reviews use a 2/3 majority threshold. Per-milestone amounts are Condorcet-averaged across auditor proposals, so an outlier auditor can't unilaterally distort the disbursement schedule.</p>
            </Mechanism>
            <Mechanism title="§3.2 — Typed deliverables">
              <p>Instead of a single bytes32 anchor, labs can submit an array of typed deliverables for each milestone. Each entry is a <code>(DeliverableType, evidenceHash, URI)</code> tuple. The contract anchors the milestone proof on <code>keccak256(abi.encode(deliverables))</code> so the on-chain commitment matches exactly what was published. Capped at 10 per milestone.</p>
              <p>The five types map to typical contract deliverables: <strong>StudyReport</strong>, <strong>DataPackage</strong>, <strong>IPAssignment</strong>, <strong>Prototype</strong>, <strong>TechTransfer</strong> — plus an <strong>Other</strong> catch-all.</p>
            </Mechanism>
            <Mechanism title="§3.3 — BARDA option-period gating">
              <p>Inspired by BARDA / ARPA-H contract patterns. Labs can submit with <code>optionExerciseRequired=true</code>, which means every milestone N ≥ 1 stays <strong>Locked</strong> after milestone N-1 is approved, until the Program Officer (the contract owner, typically OpenGovernance post-deploy) calls <code>exerciseOption(reqId, N)</code>. Milestone 0 still auto-unlocks at <code>acceptVerdict</code>, analogous to BARDA's base period being awarded at contract execution.</p>
            </Mechanism>
            <Mechanism title="DSMB veto lane">
              <p>For ClinicalData milestones, a firm holding both the <strong>ClinicalSafety</strong> category and an oracle-granted <strong>dsmbVetoRights</strong> flag can call <code>dsmbVeto(reqId, milestone)</code>. This forces the milestone into Disputed state on patient-safety grounds. The lab can then resubmit (which clears the veto for one remediation pass).</p>
            </Mechanism>
            <Mechanism title="Slashing tiers">
              <p>Bad-faith or negligent auditing has graded consequences:</p>
              <ul>
                <li><strong>Minor</strong> (procedural miss): 3% / 5% / 10% of stake burned, escalating per strike.</li>
                <li><strong>Major</strong> (procedural breach): 10% / 25% / 50%.</li>
                <li><strong>Critical</strong> (fraud / gross negligence): 100% + permanent ban from the registry.</li>
              </ul>
              <p>Slashes happen automatically when an auditor's vote ends up in the minority of a 2/3 consensus (via <code>recordOutcome</code>), and manually via owner (governance) action when compliance incidents arise off-chain.</p>
            </Mechanism>
          </Section>

          <Section id="glossary" title="Glossary — cross-domain Rosetta stone">
            <p>Metha sits at the intersection of crypto, pharma, research, and audit. Each field has its own vocabulary; this section translates.</p>
            <Glossary entries={[
              { term: 'METH', defs: { crypto: 'The protocol\'s ERC20Votes token, minted at the auction and used for governance + hype voting.' }},
              { term: 'BARDA', defs: { pharma: 'US Biomedical Advanced Research and Development Authority. Pioneered the option-period contract pattern Metha\'s §3.3 emulates.' }},
              { term: 'DSMB', defs: {
                pharma: 'Data Safety Monitoring Board — an independent group that can halt a clinical trial on safety grounds.',
                crypto: 'In Metha, a firm with attested DSMB authority can call dsmbVeto() to force a ClinicalData milestone into Disputed state.',
              }},
              { term: 'Snapshot voting', defs: { crypto: 'Vote weight is determined by token balance at a fixed past block, not at vote time. Prevents buying tokens just to vote on an active proposal.' }},
              { term: 'Slashing', defs: {
                crypto: 'Burning a portion of a participant\'s staked tokens as penalty for bad behavior. In Metha, applied to auditor firms via AuditorRegistryV2.slash().',
                audit: 'Equivalent to professional indemnity / negligence damages, but enforced on-chain and instant.',
              }},
              { term: 'GxP', defs: { pharma: 'Umbrella term for Good x Practice — GLP (Lab), GCP (Clinical), GMP (Manufacturing), etc. In Metha, an attestation key issuable by oracles.' }},
              { term: 'Milestone', defs: {
                crypto: 'A unit of a research request with its own ETH allocation and state machine (Locked → Unlocked → Claimed → ProofSubmitted → Approved/Disputed).',
                research: 'A discrete deliverable in a grant — typically a study phase, a publication, or a regulatory filing.',
              }},
              { term: 'Hype voting', defs: { crypto: 'METH-weighted prioritization of research requests. Doesn\'t bind auditor decisions but influences attention.' }},
              { term: 'Seniority tier', defs: { audit: 'Junior / Senior / Principal — Metha\'s tiered auditor structure with escalating stake minimums and milestone value caps.' }},
              { term: 'EternalStorage', defs: { crypto: 'A parameter-store contract whose slots can be rebound by governance proposals. Lets immutable contracts behave like upgradeable ones for config changes.' }},
              { term: 'Period', defs: { crypto: 'A 180-second auction window in Metha. Each period mints a fixed amount of METH split 70%/20%/10% between contributors, operations stash, and dev stash.' }},
            ]} />
          </Section>

          <Section id="next" title="Next steps">
            <Bullets items={[
              <>Read the whitepaper + due diligence in the <a className="acc" href="/#papers">Papers section</a>.</>,
              <>Launch the dapp at <code>sepolia.metha.life</code>.</>,
              <>Read the full knowledge base (EN/ES/FR) at <code>docs.metha.life</code>.</>,
              <>Browse the source on GitHub: <code>metha-api</code> (contracts), <code>metha-client</code> (landing), <code>metha-admin-v2</code> (dapp), <code>metha-docs</code> (docs).</>,
              <>Reach out: <code>info@metha.life</code> with a subject prefix like <code>[support]</code> or <code>[partnerships]</code>.</>,
            ]} />
          </Section>

        </article>
      </main>
    </div>
  );
}

/* ----- helpers ----- */

function Section({ id, title, children }) {
  return (
    <section className="docs-section" id={id}>
      <h2><span className="docs-anchor" aria-hidden="true">§</span> {title}</h2>
      {children}
    </section>
  );
}

function Bullets({ items }) {
  return (
    <ul className="docs-list">
      {items.map((it, i) => <li key={i}>{it}</li>)}
    </ul>
  );
}

function Numbered({ items }) {
  return (
    <ol className="docs-list docs-list-ol">
      {items.map((it, i) => <li key={i}>{it}</li>)}
    </ol>
  );
}

function Callout({ tone = 'ctx', children }) {
  return <aside className={`docs-callout docs-callout-${tone}`}>{children}</aside>;
}

function RoleCard({ role, persona, children }) {
  return (
    <div className="docs-role-card">
      <div className="docs-role-head">
        <span className="docs-role-name">{role}</span>
        <span className="docs-role-persona">{persona}</span>
      </div>
      <div className="docs-role-body">{children}</div>
    </div>
  );
}

function Mechanism({ title, children }) {
  return (
    <div className="docs-mech">
      <h3>{title}</h3>
      {children}
    </div>
  );
}

function Glossary({ entries }) {
  return (
    <dl className="docs-glossary">
      {entries.map(({ term, defs }) => (
        <React.Fragment key={term}>
          <dt>{term}</dt>
          <dd>
            {Object.entries(defs).map(([domain, text]) => (
              <p key={domain}>
                <span className="docs-glossary-domain">{domain}</span>
                {text}
              </p>
            ))}
          </dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

function DocsSideNav({ active }) {
  const items = [
    { id: 'overview',   label: 'Big picture' },
    { id: 'how',        label: 'On-chain flow' },
    { id: 'roles',      label: 'By role' },
    { id: 'mechanisms', label: 'Mechanisms' },
    { id: 'glossary',   label: 'Glossary' },
    { id: 'next',       label: 'Next steps' },
  ];
  return (
    <nav className="docs-sidenav" aria-label="Documentation sections">
      <span className="docs-sidenav-label">CONTENTS</span>
      {items.map((it) => (
        <a key={it.id} href={`#${it.id}`} className={active === it.id ? 'active' : ''}>
          {it.label}
        </a>
      ))}
    </nav>
  );
}
